import React, { useState, useEffect, useRef, useCallback } from 'react';

interface LinkedInViewProps {
  brand: string;
}

interface HistoryFile {
  filename: string;
  date: string;
  downloadUrl: string;
}

interface StatusData {
  today: string;
  hasUpload: boolean;
  hasComments: boolean;
  isQueued: boolean;
  queueStatus: string | null;
}

export default function LinkedInView({ brand }: LinkedInViewProps) {
  const [status, setStatus] = useState<StatusData | null>(null);
  const [history, setHistory] = useState<HistoryFile[]>([]);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [uploadResult, setUploadResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchStatus = useCallback(() => {
    fetch(`/api/${brand}/linkedin/status`).then(r => r.json()).then(setStatus).catch(() => {});
    fetch(`/api/${brand}/linkedin/history`).then(r => r.json()).then(d => setHistory(d.files || [])).catch(() => {});
  }, [brand]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // Poll status while generating
  useEffect(() => {
    if (!generating) return;
    const interval = setInterval(() => {
      fetch(`/api/${brand}/linkedin/status`).then(r => r.json()).then(s => {
        setStatus(s);
        if (s.hasComments) {
          setGenerating(false);
          fetchStatus();
        }
      });
    }, 3000);
    return () => clearInterval(interval);
  }, [generating, brand, fetchStatus]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    setUploadResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`/api/${brand}/linkedin/upload`, { method: 'POST', body: formData });
      const data = await res.json();
      if (data.ok) {
        setUploadResult(`Uploaded ${file.name} (${data.postCount > 0 ? data.postCount + ' posts' : 'XLSX file'})`);
        fetchStatus();
      } else {
        setError(data.error || 'Upload failed');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`/api/${brand}/linkedin/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!data.ok) {
        setError(data.error || 'Failed to queue');
        setGenerating(false);
      }
    } catch (err) {
      setError((err as Error).message);
      setGenerating(false);
    }
  };

  return (
    <main className="main-content" style={{ padding: '2rem' }}>
      <h1 style={{ fontSize: '1.75rem', fontWeight: 700, marginBottom: '0.5rem' }}>LinkedIn Comments</h1>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '2rem' }}>
        Upload your LinkedIn post discovery CSV, generate AI comments, and download the result.
      </p>

      {/* Status Banner */}
      {status && (
        <div style={{
          display: 'flex', gap: '1rem', marginBottom: '2rem', flexWrap: 'wrap',
        }}>
          <StatusPill label="Today" value={status.today} />
          <StatusPill label="Upload" value={status.hasUpload ? 'Ready' : 'None'} color={status.hasUpload ? 'var(--success)' : 'var(--text-tertiary)'} />
          <StatusPill label="Comments" value={status.hasComments ? 'Ready' : status.isQueued ? 'Generating...' : 'Not generated'} color={status.hasComments ? 'var(--success)' : status.isQueued ? 'var(--warning)' : 'var(--text-tertiary)'} />
        </div>
      )}

      {/* Upload Section */}
      <section style={{
        border: '2px dashed var(--border)', borderRadius: 0, padding: '2rem',
        textAlign: 'center', marginBottom: '2rem', background: 'var(--bg-secondary)',
      }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx"
          onChange={handleUpload}
          style={{ display: 'none' }}
        />
        <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📤</div>
        <p style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
          {uploading ? 'Uploading...' : 'Drop your LinkedIn post CSV/XLSX here'}
        </p>
        <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginBottom: '1rem' }}>
          From the daily LinkedIn discovery task (LinkedIn_Cloud_Thread_Tracker.xlsx)
        </p>
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          style={{
            background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 0,
            padding: '0.6rem 1.5rem', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem',
          }}
        >
          {uploading ? 'Uploading...' : 'Choose File'}
        </button>
        {uploadResult && <p style={{ color: 'var(--success)', marginTop: '0.75rem', fontWeight: 500 }}>{uploadResult}</p>}
        {error && <p style={{ color: 'var(--danger)', marginTop: '0.75rem' }}>{error}</p>}
      </section>

      {/* Generate Button */}
      {status?.hasUpload && (
        <section style={{ marginBottom: '2rem' }}>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              background: generating ? 'var(--text-tertiary)' : 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 0, padding: '0.75rem 2rem', fontWeight: 700, cursor: generating ? 'default' : 'pointer',
              fontSize: '1rem', width: '100%',
            }}
          >
            {generating ? '⏳ Generating comments... (this takes 2-3 min)' : '🤖 Generate AI Comments'}
          </button>
          {generating && (
            <p style={{ color: 'var(--text-tertiary)', fontSize: '0.85rem', marginTop: '0.5rem', textAlign: 'center' }}>
              Claude Code is analyzing each post and writing personalized comments. The page will update automatically when done.
            </p>
          )}
        </section>
      )}

      {/* Download Section */}
      {status?.hasComments && (
        <section style={{
          border: '1px solid var(--border)', borderRadius: 0, padding: '1.5rem',
          marginBottom: '2rem', background: 'var(--success-light)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontWeight: 700, margin: 0, color: 'var(--success)' }}>✅ Comments Ready!</h3>
              <p style={{ color: 'var(--success)', margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                AI-generated comments for today's LinkedIn posts are ready to download.
              </p>
            </div>
            <a
              href={`/api/${brand}/linkedin/download/${status.today}-comments.csv`}
              download
              style={{
                background: 'var(--success)', color: '#fff', border: 'none', borderRadius: 0,
                padding: '0.6rem 1.5rem', fontWeight: 600, textDecoration: 'none', fontSize: '0.9rem',
              }}
            >
              📥 Download CSV
            </a>
          </div>
        </section>
      )}

      {/* History */}
      {history.length > 0 && (
        <section>
          <h2 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.75rem' }}>History</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                <th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>Date</th>
                <th style={{ textAlign: 'left', padding: '0.5rem 0', fontWeight: 600 }}>File</th>
                <th style={{ textAlign: 'right', padding: '0.5rem 0', fontWeight: 600 }}>Download</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h) => (
                <tr key={h.filename} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '0.5rem 0' }}>{h.date}</td>
                  <td style={{ padding: '0.5rem 0', color: 'var(--text-secondary)' }}>{h.filename}</td>
                  <td style={{ padding: '0.5rem 0', textAlign: 'right' }}>
                    <a href={h.downloadUrl} download style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                      Download
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </main>
  );
}

function StatusPill({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 0,
      padding: '0.5rem 1rem', display: 'flex', gap: '0.5rem', alignItems: 'center',
    }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--text-tertiary)', textTransform: 'uppercase', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 600, color: color || 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}
